import { Link as RouterLink } from '@tanstack/react-router';
import type { PollerEmailDecision } from '@app/shared';
import {
  Button,
  DataTable,
  EmptyState,
  Link,
  StatusBadge,
  Text,
  type Column,
} from '../ui';
import { absoluteTime, decisionMeta, reasonLabel, relativeTime, shortId } from '../lib/pollerActivity';

/**
 * The scannable per-email decision table (#318), shared by the run-detail drill-down and the
 * Poller Activity lookup. One row per ingested email: WHAT the poller decided (a color-coded
 * badge), WHY (the non-actionable reason), and enough of the email (subject / from / thread) +
 * timing to answer "why didn't my agent reply to X". Reflows to stacked cards below `md`
 * (DataTable), so it never forces the page to scroll sideways.
 */
export interface PollerDecisionsTableProps {
  decisions: PollerEmailDecision[];
  /** Accessible caption (visually hidden). */
  caption: string;
  /** Empty-state copy when there are no decisions. */
  emptyLabel: string;
  /**
   * When provided, the connection each email arrived on renders as a button that focuses the
   * connection-health panel on it (the lookup screen). Omitted in the run drill-down.
   */
  onConnectionClick?: (connectionId: string) => void;
}

export function PollerDecisionsTable({
  decisions,
  caption,
  emptyLabel,
  onConnectionClick,
}: PollerDecisionsTableProps) {
  const columns: Column<PollerEmailDecision>[] = [
    {
      id: 'decision',
      header: 'Decision',
      hideCardLabel: true,
      cell: (d) => {
        const meta = decisionMeta(d.decision);
        return <StatusBadge size="sm" tone={meta.tone} label={meta.label} />;
      },
    },
    {
      id: 'reason',
      header: 'Reason',
      cell: (d) => (
        <Text as="span" size="sm" tone={d.reason ? 'secondary' : 'muted'}>
          {reasonLabel(d.reason) ?? '—'}
        </Text>
      ),
    },
    {
      // Subject is the operator's primary "which email is this" signal, so it claims the lion's
      // share of the row. A percentage `width` is a real proportional hint in the table's auto
      // layout — the old `minmax(0, 1.4fr)` was grid-only syntax the table ignored, which is why
      // the column collapsed to a few characters. `maxWidth` toggles the cell's overflow clip so
      // the `truncate` span can ellipsize; the full subject stays discoverable via the `title`.
      id: 'subject',
      header: 'Subject',
      width: '50%',
      maxWidth: '0',
      cell: (d) => (
        <Text as="span" size="sm" truncate title={d.subject ?? undefined}>
          {d.subject ?? <Text as="span" tone="muted">(no subject)</Text>}
        </Text>
      ),
    },
    {
      id: 'from',
      header: 'From',
      width: '24%',
      maxWidth: '0',
      cell: (d) => (
        <Text as="span" size="sm" tone="secondary" truncate title={d.fromAddress ?? undefined}>
          {d.fromAddress ?? '—'}
        </Text>
      ),
    },
    {
      // Opaque UUID — a short prefix + ellipsis is enough to correlate; the full id is on hover.
      id: 'thread',
      header: 'Thread',
      cell: (d) =>
        d.threadId ? (
          <Text as="span" size="sm" tone="muted" title={d.threadId} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {shortId(d.threadId, 6)}
          </Text>
        ) : (
          <Text as="span" size="sm" tone="muted">
            —
          </Text>
        ),
    },
    {
      id: 'received',
      header: 'Received',
      align: 'end',
      cell: (d) => (
        <Text as="span" size="sm" tone="secondary" title={absoluteTime(d.occurredAt)}>
          {relativeTime(d.occurredAt)}
        </Text>
      ),
    },
    {
      id: 'run',
      header: 'Run',
      align: 'end',
      hideCardLabel: true,
      cell: (d) =>
        d.runId ? (
          <Link asChild>
            {/*
              The router link is the CHILD (chakra's Slot), not `as={RouterLink}` (#322): chakra's
              `as` forwarding drops non-DOM object props, so `params`/`search` never reached the
              link — the route couldn't resolve `$runId`, hence the "unexpected error". As the child
              its props flow untouched. `search.ws` scopes the run-detail fetch to the DECISION'S
              workspace: the operator lookup is cross-tenant, so without it the run page defaults to
              the operator's OWN workspace and a foreign-workspace run fails to load.
            */}
            <RouterLink
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              {...({ to: '/app/runs/$runId/full', params: { runId: d.runId }, search: { ws: d.workspaceId } } as any)}
              data-testid="poller-decision-run-link"
            >
              View run
            </RouterLink>
          </Link>
        ) : (
          <Text as="span" size="sm" tone="muted">
            —
          </Text>
        ),
    },
  ];

  if (onConnectionClick) {
    columns.splice(5, 0, {
      id: 'connection',
      header: 'Connection',
      cell: (d) =>
        d.connectionId ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onConnectionClick(d.connectionId as string)}
            title={`Show fetch health for ${d.connectionId}`}
            data-testid="poller-decision-connection"
          >
            {shortId(d.connectionId, 6)}
          </Button>
        ) : (
          <Text as="span" size="sm" tone="muted">
            —
          </Text>
        ),
    });
  }

  return (
    <DataTable
      columns={columns}
      rows={decisions}
      getRowId={(d) => d.id}
      caption={caption}
      rowTestId="poller-decision-row"
      isEmpty={decisions.length === 0}
      emptyState={<EmptyState title={emptyLabel} />}
    />
  );
}
